import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Check, MessageCircle } from 'lucide-react';
import { Button } from './ui';
const fade = (delay: number) => ({
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.7,
    delay,
    ease: 'easeOut' as const
  }
});
export function Hero() {
  return <section className="relative min-h-screen flex items-center overflow-hidden bg-noronha-volcanic" data-id="element-189">
      {/* Clean minimal background — subtle vignette only, no media */}
      <div className="absolute inset-0 z-0 pointer-events-none" data-id="element-190">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(31,163,122,0.07)_0%,transparent_60%)]" data-id="element-191" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_100%,rgba(14,59,51,0.4)_0%,transparent_70%)]" data-id="element-192" />
      </div>

      <div className="container relative z-10 mx-auto px-6 pt-36 pb-16 sm:pt-44 sm:pb-20 md:pt-48 md:pb-28" data-id="element-193">
        <div className="max-w-2xl mx-auto flex flex-col items-center text-center" data-id="element-194">
          {/* Refined exclusive label badge */}
          <motion.div {...fade(0.05)} className="mb-4 md:mb-6 inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-noronha-emerald/40 bg-noronha-emerald/5" data-id="element-195">
            <Check className="w-3.5 h-3.5 text-noronha-emerald" strokeWidth={3} data-id="element-196" />
            <span className="font-heading font-bold text-[11px] sm:text-xs tracking-[0.08em] text-noronha-emerald uppercase" data-id="element-197">
              Viaje grupal exclusivo
            </span>
          </motion.div>

          {/* Title — stacked, NORONHA as the dominant word */}
          <motion.h1 {...fade(0.15)} className="mb-3 md:mb-6 font-heading font-bold text-noronha-text-heading uppercase leading-[0.88] tracking-tight flex flex-col items-center" data-id="element-198">
            <span className="text-2xl sm:text-3xl md:text-4xl tracking-[0.18em] text-noronha-text-body font-semibold" data-id="element-199">
              Fernando
            </span>
            <span className="text-base sm:text-xl md:text-2xl tracking-[0.3em] text-noronha-text-body/70 font-medium" data-id="element-200">
              De
            </span>
            <span className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl tracking-tight" data-id="element-201">
              Noronha
            </span>
            <span className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl text-noronha-emerald" data-id="element-202">
              2026
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p {...fade(0.3)} className="text-base sm:text-xl md:text-2xl text-noronha-text-body max-w-[20rem] sm:max-w-xl mb-1.5" data-id="element-203">
            Una semana de buceo, naturaleza y aventura en uno de los destinos
            más exóticos del planeta.
          </motion.p>

          {/* Highlight */}
          <motion.p {...fade(0.4)} className="text-base sm:text-lg md:text-xl text-noronha-sand font-medium italic mb-4 md:mb-6" data-id="element-204">
            Donde la naturaleza es la protagonista.
          </motion.p>

          {/* Strategic info block */}
          <motion.div {...fade(0.5)} className="mb-5 md:mb-8 flex flex-col gap-1.5 md:gap-2 items-center" data-id="element-205">
            <p className="font-heading font-semibold text-sm md:text-lg tracking-wide text-noronha-emerald uppercase" data-id="element-206">
              Viaje grupal exclusivo organizado por Buceo y Navegación
            </p>
            <p className="text-sm md:text-lg text-noronha-text-body" data-id="element-207">
              17 al 24 de noviembre de 2026
            </p>
          </motion.div>

          {/* CTAs — stacked, full width (Bonaire structure) */}
          <motion.div {...fade(0.6)} className="flex flex-col gap-3 w-full max-w-md" data-id="element-208">
            <Button variant="primary" className="w-full justify-center text-base px-8 py-4" data-id="element-209">
              <span className="text-lg leading-none" data-id="element-210">🤿</span>
              Quiero sumarme al grupo
            </Button>
            <Button variant="secondary" className="w-full justify-center text-base px-8 py-4" data-id="element-211">
              <MessageCircle className="w-5 h-5" strokeWidth={1.75} data-id="element-212" />
              Hablar por WhatsApp
            </Button>
          </motion.div>

          {/* Availability indicator */}
          <motion.div {...fade(0.7)} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-noronha-gold/15 border border-noronha-gold/50" data-id="element-213">
            <span className="text-base leading-none" data-id="element-214">🔥</span>
            <span className="font-heading font-bold text-sm sm:text-base tracking-[0.12em] text-noronha-text-heading uppercase whitespace-nowrap" data-id="element-215">
              Quedan 6 lugares disponibles
            </span>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.2,
      duration: 1
    }} className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-noronha-text-body/50" data-id="element-216">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em]" data-id="element-217">
          Descubrir
        </span>
        <motion.div animate={{
        y: [0, 8, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} data-id="element-218">
          <ChevronDown className="w-5 h-5" data-id="element-219" />
        </motion.div>
      </motion.div>
    </section>;
}